import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import EventRepeatRoundedIcon from '@mui/icons-material/EventRepeatRounded'
import PersonPinCircleOutlinedIcon from '@mui/icons-material/PersonPinCircleOutlined'
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined'
import SwapHorizRoundedIcon from '@mui/icons-material/SwapHorizRounded'
import SyncAltRoundedIcon from '@mui/icons-material/SyncAltRounded'
import TuneRoundedIcon from '@mui/icons-material/TuneRounded'
import VideocamOutlinedIcon from '@mui/icons-material/VideocamOutlined'
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'react'
import { toast } from 'react-toastify'
import {
  changeSupportBookedEventMode,
  markSupportBookedEventAsTest,
  markSupportBookedEventCompleted,
} from 'src/supportPortal/services/supportBookedEventActionsService'
import { SupportActionMenuButton } from './SupportBookingCardActionMenus'

function isBookedEventCompleted(bookedEvent) {
  return Boolean(
    bookedEvent?.markedCompleted ||
      bookedEvent?.sessionStatus === 'marked-completed' ||
      bookedEvent?.sessionStatus === 'finished',
  )
}

/**
 * The per-card "Manage" menu on the support booked-events page: reschedule, reassign,
 * mark completed, engagement, the test-booking flag and cancel — all the state-changing
 * booking actions in one grouped menu. Reschedule/cancel/engagement handlers come from the
 * card via `supportContext`; heavier flows (reassign drawer, mark-paid dialog) are lifted to
 * the page via callbacks. Owns only the lightweight mark-completed confirm dialog.
 */
export default function SupportBookedEventManageMenu({
  bookedEvent,
  supportContext = null,
  // Role-API gate for the state-changing items that hit /events/actions/* (reassign,
  // mark-completed, test flag). Reschedule/cancel/engagement come from `supportContext`
  // and carry their own card-side gating.
  canManageActions = false,
  onReassign,
  onActionSuccess,
}) {
  const [confirmCompleteOpen, setConfirmCompleteOpen] = useState(false)
  const [completing, setCompleting] = useState(false)
  const [testToggling, setTestToggling] = useState(false)
  const [changeModeDialogOpen, setChangeModeDialogOpen] = useState(false)
  const [changingMode, setChangingMode] = useState(false)
  const [modeReason, setModeReason] = useState('')

  const isCancelled = bookedEvent?.status === 'cancelled'
  const isCompleted = isBookedEventCompleted(bookedEvent)
  const isTestEvent = Boolean(bookedEvent?.testEvent)
  const isOnline = bookedEvent?.meetingSrc === 'aumhum'
  const targetMode = isOnline ? 'offline' : 'aumhum'
  const targetModeLabel = isOnline ? 'In-Clinic' : 'Video-Call'
  const currentModeLabel = isOnline ? 'Video-Call' : 'In-Clinic'

  const reschedule = supportContext?.reschedule || null
  const cancel = supportContext?.cancel || null
  const engagement = supportContext?.engagement || null
  const canMarkCompleted =
    canManageActions && !isCancelled && !isCompleted && bookedEvent?.status === 'booked'
  const canReassign = canManageActions && !isCancelled && !isCompleted && Boolean(onReassign)
  const canChangeMode = canManageActions && !isCancelled && !isCompleted
  const canToggleTestFlag = canManageActions

  const hasAnyItem =
    Boolean(reschedule) ||
    canReassign ||
    canMarkCompleted ||
    canChangeMode ||
    Boolean(engagement) ||
    canToggleTestFlag ||
    Boolean(cancel)

  if (!hasAnyItem) {
    return null
  }

  async function handleChangeMode() {
    setChangingMode(true)
    const result = await changeSupportBookedEventMode({
      bookedEventId: bookedEvent?.id,
      targetMode,
      reason: modeReason.trim() || undefined,
    })
    setChangingMode(false)
    if (result.ok) {
      toast.success(`Booking mode changed to ${targetModeLabel}`)
      setChangeModeDialogOpen(false)
      setModeReason('')
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  async function handleMarkCompleted() {
    setCompleting(true)
    const result = await markSupportBookedEventCompleted(bookedEvent?.id)
    setCompleting(false)
    setConfirmCompleteOpen(false)
    if (result.ok) {
      toast.success('Session marked completed')
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  async function handleToggleTestFlag(closeMenu) {
    setTestToggling(true)
    const result = await markSupportBookedEventAsTest(bookedEvent?.id, !isTestEvent)
    setTestToggling(false)
    closeMenu?.()
    if (result.ok) {
      toast.success(isTestEvent ? 'Test flag removed' : 'Marked as a test booking')
      onActionSuccess?.()
    } else {
      toast.error(result.message)
    }
  }

  return (
    <>
      <SupportActionMenuButton
        label="Manage"
        icon={<TuneRoundedIcon sx={{ fontSize: 15 }} />}
        title="Reschedule, reassign, complete or cancel this booking"
      >
        {reschedule
          ? (close) => (
              <MenuItem
                key="reschedule"
                onClick={() => {
                  close()
                  reschedule.onClick?.()
                }}
              >
                <ListItemIcon>
                  <EventRepeatRoundedIcon fontSize="small" sx={{ color: '#4f46e5' }} />
                </ListItemIcon>
                <ListItemText primary={reschedule.label || 'Reschedule'} />
              </MenuItem>
            )
          : null}
        {canReassign
          ? (close) => (
              <MenuItem
                key="reassign"
                onClick={() => {
                  close()
                  onReassign(bookedEvent)
                }}
              >
                <ListItemIcon>
                  <SwapHorizRoundedIcon fontSize="small" sx={{ color: '#0e7490' }} />
                </ListItemIcon>
                <ListItemText primary="Reassign practitioner" />
              </MenuItem>
            )
          : null}
        {canMarkCompleted
          ? (close) => (
              <MenuItem
                key="mark-completed"
                onClick={() => {
                  close()
                  setConfirmCompleteOpen(true)
                }}
              >
                <ListItemIcon>
                  <CheckCircleOutlineRoundedIcon fontSize="small" sx={{ color: '#047857' }} />
                </ListItemIcon>
                <ListItemText primary="Mark completed" secondary="On behalf of the practitioner" />
              </MenuItem>
            )
          : null}
        {canChangeMode
          ? (close) => (
              <MenuItem
                key="change-mode"
                onClick={() => {
                  close()
                  setModeReason('')
                  setChangeModeDialogOpen(true)
                }}
              >
                <ListItemIcon>
                  {isOnline ? (
                    <PersonPinCircleOutlinedIcon fontSize="small" sx={{ color: '#0369a1' }} />
                  ) : (
                    <VideocamOutlinedIcon fontSize="small" sx={{ color: '#0369a1' }} />
                  )}
                </ListItemIcon>
                <ListItemText
                  primary={`Change to ${targetModeLabel}`}
                  secondary={`Currently ${currentModeLabel}`}
                />
              </MenuItem>
            )
          : null}
        {engagement
          ? (close) => (
              <MenuItem
                key="engagement"
                onClick={() => {
                  close()
                  engagement.onClick?.()
                }}
              >
                <ListItemIcon>
                  <VisibilityOutlinedIcon fontSize="small" sx={{ color: '#6d28d9' }} />
                </ListItemIcon>
                <ListItemText primary="Engagement settings" />
              </MenuItem>
            )
          : null}
        {canToggleTestFlag ? <Divider key="manage-divider-1" /> : null}
        {canToggleTestFlag
          ? (close) => (
              <MenuItem
                key="test-flag"
                disabled={testToggling}
                onClick={() => handleToggleTestFlag(close)}
              >
                <ListItemIcon>
                  <ScienceOutlinedIcon fontSize="small" sx={{ color: '#b45309' }} />
                </ListItemIcon>
                <ListItemText
                  primary={isTestEvent ? 'Remove test flag' : 'Mark as test booking'}
                  secondary={isTestEvent ? 'Counted in stats again' : 'Excluded from stats'}
                />
              </MenuItem>
            )
          : null}
        {cancel ? <Divider key="manage-divider-2" /> : null}
        {cancel
          ? (close) => (
              <MenuItem
                key="cancel"
                onClick={() => {
                  close()
                  cancel.onClick?.()
                }}
                sx={{ '& .MuiListItemText-primary': { color: '#b91c1c !important' } }}
              >
                <ListItemIcon>
                  <CancelOutlinedIcon fontSize="small" sx={{ color: '#b91c1c' }} />
                </ListItemIcon>
                <ListItemText primary={cancel.label || 'Cancel booking'} />
              </MenuItem>
            )
          : null}
      </SupportActionMenuButton>

      <Dialog
        open={confirmCompleteOpen}
        onClose={completing ? undefined : () => setConfirmCompleteOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Mark this session completed?</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            This finishes the session on behalf of the practitioner and triggers the usual
            post-session flows. It cannot be undone from this page.
          </Alert>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setConfirmCompleteOpen(false)} disabled={completing}>
            Cancel
          </Button>
          <Button variant="contained" color="success" onClick={handleMarkCompleted} disabled={completing}>
            {completing ? 'Marking...' : 'Mark Completed'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={changeModeDialogOpen}
        onClose={changingMode ? undefined : () => setChangeModeDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: '14px' } }}
      >
        <DialogTitle sx={{ fontWeight: 800 }}>Change session mode?</DialogTitle>
        <DialogContent>
          <Stack spacing={1.5} sx={{ mt: 0.5 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ justifyContent: 'center', py: 0.5 }}>
              <Box
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: '10px',
                  border: '1px solid rgba(148, 163, 184, 0.4)',
                  backgroundColor: '#f8fafc',
                  minWidth: 104,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>
                  From
                </Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 750, color: '#475569' }}>
                  {currentModeLabel}
                </Typography>
              </Box>
              <SyncAltRoundedIcon sx={{ color: '#4f46e5', fontSize: 20 }} />
              <Box
                sx={{
                  px: 1.25,
                  py: 0.75,
                  borderRadius: '10px',
                  border: '1px solid rgba(79, 70, 229, 0.3)',
                  backgroundColor: 'rgba(79, 70, 229, 0.06)',
                  minWidth: 104,
                  textAlign: 'center',
                }}
              >
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: '#818cf8', textTransform: 'uppercase' }}>
                  To
                </Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 800, color: '#4338ca' }}>
                  {targetModeLabel}
                </Typography>
              </Box>
            </Stack>
            <Alert severity="info" sx={{ borderRadius: 2, fontSize: 12.5 }}>
              {isOnline
                ? 'The booking becomes an in-person clinic session. The video joining links stop applying; a chamber room is assigned when the practitioner checks in.'
                : 'The booking becomes an AUMHUM video call and a fresh joining link is generated. Use "Send join link" afterwards to share it with the patient.'}
            </Alert>
            <TextField
              label="Reason (optional)"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={modeReason}
              onChange={(event) => setModeReason(event.target.value)}
              placeholder="Recorded in the support activity trail"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button color="inherit" onClick={() => setChangeModeDialogOpen(false)} disabled={changingMode}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleChangeMode} disabled={changingMode}>
            {changingMode ? 'Changing...' : `Change to ${targetModeLabel}`}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
